import { useAuth } from "@/src/contexts/auth.context";
import {
  FeatureRequestMutationResult,
  FeatureRequestPage,
  FeatureRequestStatus,
  GetFeatureRequestsResponse,
  InsertFeatureRequestInput,
  UpdateFeatureRequestInput,
} from "@/src/models/feature-request.model";
import { FEATURE_REQUESTS_QUERY_KEY } from "@/src/query-keys/feature-request.keys";
import gqlClient from "@/src/services/api/graphql.client";
import {
  GET_FEATURE_REQUESTS_QUERY,
  INSERT_FEATURE_REQUEST_MUTATION,
  UPDATE_FEATURE_REQUEST_MUTATION,
} from "@/src/services/feature-request/feature-request.query";
import {
  createFeatureRequestIssue,
  updateFeatureRequestIssue,
} from "@/src/services/feature-request/feature-request.webhook";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";

const COLUMN_PAGE_SIZE = 20;

export type FeatureRequestScope = Readonly<{
  /** Restrict to these creators. Empty/undefined means everyone. */
  createdBy?: readonly string[];
  search?: string;
}>;

const buildWhere = (
  status: FeatureRequestStatus,
  { createdBy, search }: FeatureRequestScope,
): object => {
  const where: Record<string, unknown> = { status: { eq: status } };
  if (createdBy?.length) {
    where.CreatedBy =
      createdBy.length === 1 ? { eq: createdBy[0] } : { in: [...createdBy] };
  }
  const term = search?.trim();
  if (term) {
    where.or = [
      { title: { contains: term } },
      { description: { contains: term } },
    ];
  }
  return where;
};

const fetchColumnPage = async (
  where: object,
  pageNo: number,
): Promise<FeatureRequestPage> => {
  const client = await gqlClient();
  const data = await client.request<GetFeatureRequestsResponse>(
    GET_FEATURE_REQUESTS_QUERY,
    {
      where,
      order: [{ field: "CreatedDate", direction: "DESC" }],
      pageNo,
      pageSize: COLUMN_PAGE_SIZE,
    },
  );
  return {
    items: data.getfeatureRequests?.items ?? [],
    totalCount: data.getfeatureRequests?.totalCount ?? 0,
  };
};

/**
 * One board column, paged 20 at a time. Each column is its own query so the
 * header count is that status's real `totalCount`, not just what's loaded.
 */
export const useFeatureRequestColumn = (
  status: FeatureRequestStatus,
  scope: FeatureRequestScope,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: [
      ...FEATURE_REQUESTS_QUERY_KEY,
      status,
      [...(scope.createdBy ?? [])].sort().join(","),
      scope.search?.trim() ?? "",
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchColumnPage(buildWhere(status, scope), pageParam as number),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((n, p) => n + p.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    enabled,
  });

export const useCreateFeatureRequest = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: InsertFeatureRequestInput) => {
      const issue = await createFeatureRequestIssue(input);
      const client = await gqlClient();
      const data = await client.request<{
        insertfeatureRequest: FeatureRequestMutationResult;
      }>(INSERT_FEATURE_REQUEST_MUTATION, {
        input: {
          ...input,
          ...issue,
          status: "open" satisfies FeatureRequestStatus,
          authorSnapshot: {
            firstName: user?.firstName ?? "",
            lastName: user?.lastName ?? "",
            email: user?.email ?? "",
            profileImageUrl: user?.profileImageUrl ?? "",
          },
        },
      });
      return data.insertfeatureRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEATURE_REQUESTS_QUERY_KEY });
      Toast.show({
        type: "success",
        text1: t("SUCCESS_FEATURE_REQUEST_CREATED"),
      });
    },
    onError: () => {
      Toast.show({
        type: "error",
        text1: t("ERROR_FEATURE_REQUEST_CREATE_FAILED"),
      });
    },
  });
};

export const useUpdateFeatureRequest = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ itemId, title, description }: UpdateFeatureRequestInput) => {
      const client = await gqlClient();
      const data = await client.request<{
        updatefeatureRequest: FeatureRequestMutationResult;
      }>(UPDATE_FEATURE_REQUEST_MUTATION, { itemId, title, description });
      return data.updatefeatureRequest;
    },
    onSuccess: (_result, { issueNumber, title, description }) => {
      queryClient.invalidateQueries({ queryKey: FEATURE_REQUESTS_QUERY_KEY });
      Toast.show({
        type: "success",
        text1: t("SUCCESS_FEATURE_REQUEST_UPDATED"),
      });
      if (issueNumber) {
        void updateFeatureRequestIssue({ issueNumber, title, description });
      }
    },
    onError: () => {
      Toast.show({
        type: "error",
        text1: t("ERROR_FEATURE_REQUEST_UPDATE_FAILED"),
      });
    },
  });
};
